import React, { useEffect, useState, useRef, Children } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
function AnimatedNumber({
  value,
  suffix,
  duration = 2
}: {
  value: number;
  suffix: string;
  duration?: number;
}) {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, v => {
    if (value < 10) return Number(v.toFixed(1));
    return Math.round(v);
  });
  useEffect(() => {
    if (isInView) {
      const controls = animate(motionValue, value, {
        duration,
        ease: [0.22, 1, 0.36, 1]
      });
      return controls.stop;
    }
  }, [isInView, value, duration, motionValue]);
  useEffect(() => {
    const unsubscribe = rounded.on('change', v => setDisplayValue(v));
    return unsubscribe;
  }, [rounded]);
  return <span ref={ref} data-id="element-1034">
      {displayValue.toLocaleString()}
      {suffix}
    </span>;
}
export function StatsDMRV() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const statVariant = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section ref={ref} className="w-full bg-carbon-deep py-20 sm:py-24" data-id="element-1035">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1036">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-1037">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight" data-id="element-1038">
            Built for Africa's agricultural supply chains
          </h2>
          <p className="mt-3 text-lg text-white/60" data-id="element-1039">
            Powering verifiable trade compliance across East Africa's most
            critical export sectors.
          </p>
        </motion.div>
        <motion.div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: 0.1
          }
        }
      }} data-id="element-1040">
          <motion.div className="text-center" variants={statVariant} data-id="element-1041">
            <p className="text-4xl sm:text-5xl font-extrabold text-carbon-bright" data-id="element-1042">
              <AnimatedNumber value={1.7} suffix="M+" duration={2} data-id="element-1043" />
            </p>
            <p className="mt-2 text-sm font-medium text-white/60 uppercase tracking-wider" data-id="element-1044">
              Smallholder Farmers
            </p>
            <p className="mt-1 text-xs text-white/40" data-id="element-1045">
              Across Uganda's coffee-growing regions
            </p>
          </motion.div>
          <motion.div className="text-center" variants={statVariant} data-id="element-1046">
            <p className="text-4xl sm:text-5xl font-extrabold text-carbon-bright" data-id="element-1047">
              <AnimatedNumber value={5} suffix="" duration={1.5} data-id="element-1048" />
            </p>
            <p className="mt-2 text-sm font-medium text-white/60 uppercase tracking-wider" data-id="element-1049">
              dMRV Verticals
            </p>
            <p className="mt-1 text-xs text-white/40" data-id="element-1050">
              Coffee, cookstoves, forestry, agroforestry, energy
            </p>
          </motion.div>
          <motion.div className="text-center" variants={statVariant} data-id="element-1051">
            <p className="text-4xl sm:text-5xl font-extrabold text-carbon-bright" data-id="element-1052">
              $<AnimatedNumber value={900} suffix="M+" data-id="element-1053" />
            </p>
            <p className="mt-2 text-sm font-medium text-white/60 uppercase tracking-wider" data-id="element-1054">
              EU Export Value
            </p>
            <p className="mt-1 text-xs text-white/40" data-id="element-1055">
              Uganda's annual coffee exports to the EU (USD)
            </p>
          </motion.div>
          <motion.div className="text-center" variants={statVariant} data-id="element-1056">
            <p className="text-4xl sm:text-5xl font-extrabold text-carbon-bright" data-id="element-1057">
              <AnimatedNumber value={100} suffix="%" duration={1.5} data-id="element-1058" />
            </p>
            <p className="mt-2 text-sm font-medium text-white/60 uppercase tracking-wider" data-id="element-1059">
              Traceable
            </p>
            <p className="mt-1 text-xs text-white/40" data-id="element-1060">
              Farm-to-export digital verification
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}